import React, { useEffect, useRef, useState } from "react";

// createSfxPlayer builds a lightweight synthesized sound effects player for the Pong game.
const createSfxPlayer = () => {
  // Create and manage simple synthesized sound effects for gameplay events.
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    return {
      // Ignore paddle hit sound effect requests when Web Audio is unavailable.
      playPaddleHit: () => {},
      // Ignore score sound effect requests when Web Audio is unavailable.
      playScore: () => {},
      // Ignore resume requests when Web Audio is unavailable.
      resume: () => Promise.resolve(),
      // Ignore dispose requests when Web Audio is unavailable.
      dispose: () => {},
    };
  }

  const context = new AudioContextClass();
  const master = context.createGain();
  master.gain.value = 0.18;
  master.connect(context.destination);

  const playTone = ({ type, startFreq, endFreq, duration, volume, detune = 0, startTime = context.currentTime }) => {
    // Play a single pitched tone with a quick envelope and optional frequency slide.
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(startFreq, startTime);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, endFreq), startTime + duration);
    osc.detune.value = detune;
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    osc.connect(gain);
    gain.connect(master);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.02);
  };

  const playPaddleHit = () => {
    // Play a short bright blip for a paddle hit.
    playTone({ type: "square", startFreq: 520, endFreq: 320, duration: 0.08, volume: 0.2 });
  };

  const playScore = () => {
    // Play a quick descending tone to signal a score/out-of-bounds.
    playTone({ type: "triangle", startFreq: 420, endFreq: 140, duration: 0.18, volume: 0.22 });
  };

  const resume = () => {
    // Resume the audio context after a user gesture if it is suspended.
    return context.state === "suspended" ? context.resume() : Promise.resolve();
  };

  const dispose = () => {
    // Close the audio context to release resources when the game unmounts.
    if (context.state !== "closed") {
      context.close();
    }
  };

  return {
    playPaddleHit,
    playScore,
    resume,
    dispose,
  };
};

// SBONG renders the Pong game UI and manages the game loop.
const SBONG = () => {
  const containerRef = useRef(null);
  const sizeRef = useRef({ width: 640, height: 360 });
  const playerTargetRef = useRef(180);
  const stateRef = useRef({
    ballX: 320,
    ballY: 180,
    ballVX: 180,
    ballVY: 120,
    playerY: 150,
    aiY: 150,
  });
  const sfxRef = useRef(null);
  const audioReadyRef = useRef(false);

  const [frame, setFrame] = useState({
    ballX: 320,
    ballY: 180,
    playerY: 150,
    aiY: 150,
    width: 640,
    height: 360,
  });

  const paddleWidth = 10;
  const paddleHeight = 70;
  const ballRadius = 6;
  const playerSpeed = 320;
  const aiSpeed = 260;

  // clampValue keeps a numeric value within the provided bounds.
  const clampValue = (value, min, max) => Math.max(min, Math.min(max, value));

  // resetBall centers the ball and launches it in a given horizontal direction.
  const resetBall = (direction) => {
    const { width, height } = sizeRef.current;
    stateRef.current.ballX = width / 2;
    stateRef.current.ballY = height / 2;
    stateRef.current.ballVX = direction * 200;
    stateRef.current.ballVY = (Math.random() * 160 - 80) || 80;
  };

  // updateSize reads the container size and stores it for physics calculations.
  const updateSize = () => {
    const element = containerRef.current;
    if (!element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    const width = Math.max(320, rect.width);
    const height = Math.max(240, rect.height);
    sizeRef.current = { width, height };
    setFrame((previous) => ({
      ...previous,
      width,
      height,
    }));
  };

  // handleMouseMove updates the player target position based on cursor movement.
  const handleMouseMove = (event) => {
    const element = containerRef.current;
    if (!element) {
      return;
    }
    ensureAudioResumed();
    const rect = element.getBoundingClientRect();
    const nextTarget = event.clientY - rect.top - paddleHeight / 2;
    playerTargetRef.current = clampValue(nextTarget, 0, rect.height - paddleHeight);
  };

  // handleTouchMove updates the player target position based on touch movement.
  const handleTouchMove = (event) => {
    const element = containerRef.current;
    if (!element || event.touches.length === 0) {
      return;
    }
    ensureAudioResumed();
    const rect = element.getBoundingClientRect();
    const nextTarget = event.touches[0].clientY - rect.top - paddleHeight / 2;
    playerTargetRef.current = clampValue(nextTarget, 0, rect.height - paddleHeight);
  };

  // ensureAudioResumed resumes the Web Audio context on the first user interaction.
  const ensureAudioResumed = () => {
    if (audioReadyRef.current) {
      return;
    }
    audioReadyRef.current = true;
    const resumePromise = sfxRef.current?.resume?.();
    if (resumePromise?.catch) {
      resumePromise.catch(() => {});
    }
  };

  // applyPlayerMovement moves the player paddle toward the target at a fixed speed.
  const applyPlayerMovement = (deltaSeconds, boardHeight) => {
    const current = stateRef.current.playerY;
    const target = playerTargetRef.current;
    const direction = Math.sign(target - current);
    const next = current + direction * playerSpeed * deltaSeconds;
    stateRef.current.playerY = clampValue(
      direction === 0 ? current : direction > 0 ? Math.min(next, target) : Math.max(next, target),
      0,
      boardHeight - paddleHeight
    );
  };

  // applyAiMovement moves the AI paddle to follow the ball vertically.
  const applyAiMovement = (deltaSeconds, boardHeight) => {
    const current = stateRef.current.aiY;
    const target = stateRef.current.ballY - paddleHeight / 2;
    const direction = Math.sign(target - current);
    const next = current + direction * aiSpeed * deltaSeconds;
    stateRef.current.aiY = clampValue(
      direction === 0 ? current : direction > 0 ? Math.min(next, target) : Math.max(next, target),
      0,
      boardHeight - paddleHeight
    );
  };

  // applyBallMovement moves the ball, handles wall collisions, and checks for paddle hits.
  const applyBallMovement = (deltaSeconds, boardWidth, boardHeight) => {
    const state = stateRef.current;
    state.ballX += state.ballVX * deltaSeconds;
    state.ballY += state.ballVY * deltaSeconds;

    if (state.ballY - ballRadius <= 0) {
      state.ballY = ballRadius;
      state.ballVY *= -1;
    }

    if (state.ballY + ballRadius >= boardHeight) {
      state.ballY = boardHeight - ballRadius;
      state.ballVY *= -1;
    }

    const playerX = 20;
    const aiX = boardWidth - 20 - paddleWidth;

    if (
      state.ballVX < 0 &&
      state.ballX - ballRadius <= playerX + paddleWidth &&
      state.ballY >= state.playerY &&
      state.ballY <= state.playerY + paddleHeight
    ) {
      state.ballX = playerX + paddleWidth + ballRadius;
      state.ballVX *= -1.05;
      state.ballVY += (state.ballY - (state.playerY + paddleHeight / 2)) * 3;
      sfxRef.current?.playPaddleHit?.();
    }

    if (
      state.ballVX > 0 &&
      state.ballX + ballRadius >= aiX &&
      state.ballY >= state.aiY &&
      state.ballY <= state.aiY + paddleHeight
    ) {
      state.ballX = aiX - ballRadius;
      state.ballVX *= -1.05;
      state.ballVY += (state.ballY - (state.aiY + paddleHeight / 2)) * 3;
      sfxRef.current?.playPaddleHit?.();
    }

    if (state.ballX + ballRadius < 0) {
      sfxRef.current?.playScore?.();
      resetBall(1);
    }

    if (state.ballX - ballRadius > boardWidth) {
      sfxRef.current?.playScore?.();
      resetBall(-1);
    }
  };

  // stepGame advances the entire game simulation for a single frame.
  const stepGame = (deltaSeconds) => {
    const { width, height } = sizeRef.current;
    applyPlayerMovement(deltaSeconds, height);
    applyAiMovement(deltaSeconds, height);
    applyBallMovement(deltaSeconds, width, height);
    setFrame({
      ballX: stateRef.current.ballX,
      ballY: stateRef.current.ballY,
      playerY: stateRef.current.playerY,
      aiY: stateRef.current.aiY,
      width,
      height,
    });
  };

  // runAnimationLoop starts the requestAnimationFrame loop for the game.
  const runAnimationLoop = () => {
    let lastTime = performance.now();
    let frameId = 0;

    // animateFrame is the rAF callback that keeps the game moving.
    const animateFrame = (time) => {
      const delta = Math.min(32, time - lastTime) / 1000;
      lastTime = time;
      stepGame(delta);
      frameId = requestAnimationFrame(animateFrame);
    };

    frameId = requestAnimationFrame(animateFrame);

    return () => {
      cancelAnimationFrame(frameId);
    };
  };

  useEffect(() => {
    updateSize();
    resetBall(Math.random() > 0.5 ? 1 : -1);

    if (!sfxRef.current) {
      sfxRef.current = createSfxPlayer();
    }

    // handleResize keeps the board sized with the viewport.
    const handleResize = () => {
      updateSize();
    };

    window.addEventListener("resize", handleResize);
    const stopLoop = runAnimationLoop();

    return () => {
      window.removeEventListener("resize", handleResize);
      stopLoop();
      if (sfxRef.current) {
        sfxRef.current.dispose?.();
        sfxRef.current = null;
      }
    };
  }, []);

  const boardStyles = {
    position: "relative",
    width: "100%",
    maxWidth: "720px",
    height: "50vh",
    minHeight: "280px",
    margin: "0 auto",
    backgroundColor: "#0a0a0a",
    border: "2px solid #f5f5f5",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  const paddleStyles = {
    position: "absolute",
    width: `${paddleWidth}px`,
    height: `${paddleHeight}px`,
    backgroundColor: "#f5f5f5",
  };

  const ballStyles = {
    position: "absolute",
    width: `${ballRadius * 2}px`,
    height: `${ballRadius * 2}px`,
    borderRadius: "50%",
    backgroundColor: "#f5f5f5",
  };

  const centerLineStyles = {
    position: "absolute",
    left: "50%",
    top: 0,
    transform: "translateX(-50%)",
    width: "4px",
    height: "100%",
    backgroundImage:
      "repeating-linear-gradient(to bottom, transparent 0, transparent 10px, #f5f5f5 10px, #f5f5f5 20px)",
    opacity: 0.7,
  };

  const titleStyles = {
    marginTop: "12px",
    textAlign: "center",
    color: "#f5f5f5",
    fontFamily: "'Press Start 2P', 'Courier New', monospace",
    fontSize: "18px",
    letterSpacing: "2px",
  };

  return (
    <div style={{ padding: "24px" }}>
      <div
        ref={containerRef}
        style={boardStyles}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
      >
        <div style={centerLineStyles} />
        <div
          style={{
            ...paddleStyles,
            left: "20px",
            top: `${frame.playerY}px`,
          }}
        />
        <div
          style={{
            ...paddleStyles,
            right: "20px",
            top: `${frame.aiY}px`,
          }}
        />
        <div
          style={{
            ...ballStyles,
            left: `${frame.ballX - ballRadius}px`,
            top: `${frame.ballY - ballRadius}px`,
          }}
        />
      </div>
      <div style={titleStyles}>SBONG</div>
    </div>
  );
};

export default SBONG;
